import { SxProps, Theme } from '@mui/material/styles';
import { colorPalette, fontFamily, fontSize, getSizeInPx, getSpacing } from 'stylesheet';

import { AdditionalButtonProps, ButtonDisplayStyle } from './Button';

const getButtonColor = (displayStyle: ButtonDisplayStyle) => {
  switch (displayStyle) {
    case 'filledOrange':
    case 'filledRed':
    case 'filledGreen':
    case 'filledBlue':
      return colorPalette.white;
    case 'unfilledGrey':
    case 'unfilledOrange':
      return colorPalette.greyMain;
    default:
      return undefined;
  }
};

const getButtonBorder = (displayStyle: ButtonDisplayStyle) => {
  switch (displayStyle) {
    case 'filledOrange':
    case 'unfilledOrange':
      return `solid 1px ${colorPalette.secondary_600}`;
    case 'unfilledGrey':
      return `solid 1px ${colorPalette.greyMain}`;
    case 'filledGreen':
      return `solid 1px ${colorPalette.green}`;
    case 'filledRed':
      return `solid 1px ${colorPalette.status_error}`;
    case 'filledBlue':
      return `solid 1px ${colorPalette.blue_600}`;
    default:
      return undefined;
  }
};

const getButtonBackgroundColor = (displayStyle: ButtonDisplayStyle) => {
  switch (displayStyle) {
    case 'filledOrange':
      return colorPalette.secondary_600;
    case 'filledRed':
      return colorPalette.status_error;
    case 'unfilledGrey':
    case 'unfilledOrange':
      return colorPalette.white;
    default:
      return undefined;
  }
};

const getHoverBorder = (displayStyle: ButtonDisplayStyle) => {
  switch (displayStyle) {
    case 'filledOrange':
      return `solid 1px ${colorPalette.secondary_300}`;
    case 'filledRed':
      return `solid 1px ${colorPalette.status_error_light}`;
    case 'unfilledGrey':
      return `solid 1px ${colorPalette.blue_400}`;
    case 'unfilledOrange':
      return `solid 1px ${colorPalette.secondary_600}`;
    default:
      return undefined;
  }
};

const getHoverBackgroundColor = (displayStyle: ButtonDisplayStyle) => {
  switch (displayStyle) {
    case 'filledOrange':
      return colorPalette.secondary_300;
    case 'filledRed':
      return colorPalette.status_error_light;
    case 'filledGreen':
      return colorPalette.green;
    case 'filledBlue':
      return colorPalette.blue_600;
    case 'unfilledGrey':
      return colorPalette.blue_400;
    case 'unfilledOrange':
      return colorPalette.secondary_600;
    default:
      return undefined;
  }
};

const getDisabledBorder = (displayStyle: ButtonDisplayStyle) => {
  switch (displayStyle) {
    case 'filledOrange':
    case 'filledRed':
      return `solid 1px ${colorPalette.grey_400}`;
    case 'unfilledGrey':
    case 'unfilledOrange':
      return `solid 1px ${colorPalette.grey_600}`;
    default:
      return undefined;
  }
};

const getDisabledBackgroundColor = (displayStyle: ButtonDisplayStyle) => {
  switch (displayStyle) {
    case 'filledOrange':
    case 'filledRed':
      return colorPalette.grey_400;
    case 'unfilledGrey':
    case 'unfilledOrange':
      return colorPalette.white;
    default:
      return undefined;
  }
};

const getStartIconColor = (displayStyle: ButtonDisplayStyle) => {
  switch (displayStyle) {
    case 'filledOrange':
    case 'filledRed':
      return colorPalette.white;
    case 'filledGreen':
      return colorPalette.green;
    case 'filledBlue':
      return colorPalette.blue_600;
    case 'unfilledGrey':
      return colorPalette.greyMain;
    case 'unfilledOrange':
      return colorPalette.secondary_600;
    case 'unfilledgreen':
      return colorPalette.light_green;
    default:
      return undefined;
  }
};

export const getButtonSx = ({
  margins,
  bigPaddings,
  displayStyle,
  isLoading,
  children,
  justifyContentLeft,
  widthInPx,
  allSidePadding,
  isMobile,
}: AdditionalButtonProps): SxProps<Theme> => ({
  fontFamily: fontFamily.main,
  fontSize: fontSize.p1,
  height: getSpacing(5),
  ...(widthInPx && { width: `${getSizeInPx(widthInPx)}` }),
  textTransform: 'inherit',
  letterSpacing: 'inherit',
  margin: margins ?? '0',
  padding: allSidePadding
    ? allSidePadding
    : bigPaddings
      ? `${getSpacing(1)} ${!isMobile ? getSpacing(5) : getSpacing(1)}`
      : `${getSpacing(1)} ${!isMobile ? getSpacing(4) : getSpacing(1)}`,
  borderRadius: getSpacing(1),
  '& .MuiButton-label': {
    justifyContent: justifyContentLeft ? 'flex-start' : 'inherit',
    ...(isLoading && {
      position: 'relative',
      visibility: 'hidden',
      '& .MuiCircularProgress-root': {
        visibility: 'visible',
      },
    }),
  },
  ...(isLoading && {
    pointerEvents: 'none',
  }),
  color: getButtonColor(displayStyle),
  border: getButtonBorder(displayStyle),
  backgroundColor: getButtonBackgroundColor(displayStyle),
  '&:hover,&:focus-visible,&:hover .MuiButton-startIcon,&:focus-visible .MuiButton-startIcon': {
    color: colorPalette.white,
  },
  '&:hover,&:focus-visible': {
    border: getHoverBorder(displayStyle),
    backgroundColor: getHoverBackgroundColor(displayStyle),
  },
  '&:disabled': {
    border: getDisabledBorder(displayStyle),
    backgroundColor: getDisabledBackgroundColor(displayStyle),
  },
  '&:disabled,&:disabled .MuiButton-startIcon': {
    color: colorPalette.grey_600,
  },
  '& .MuiButton-startIcon': {
    color: getStartIconColor(displayStyle),
    marginLeft: 0,
    marginRight: children ? getSpacing(2) : 0,
  },
});
